import { useCallback, useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { useLogEvent } from './useLogEvent'
import { isUnsafeToReload } from './isUnsafeToReload'
import { sendMemoryInventoryToSentry } from './memoryInventory'
import { SECONDS } from '@sequencehq/utils/dates'
import { apiQueryClient } from 'features/api/apiQueryClient'

const MEMORY_CHECK_INTERVAL = 10 * SECONDS
const MEMORY_THRESHOLD_BYTES = 3 * 1024 * 1024 * 1024 // 3GB

export const useMemoryThresholdReload = () => {
  const { logEvent } = useLogEvent()
  const { pathname } = useLocation()
  const pathnameRef = useRef(pathname)

  useEffect(() => {
    pathnameRef.current = pathname
  }, [pathname])

  const checkMemory = useCallback(() => {
    try {
      if (!performance || !('memory' in performance)) return

      const memoryUsage = performance.memory as {
        usedJSHeapSize: number
        totalJSHeapSize: number
        jsHeapSizeLimit: number
      }

      if (memoryUsage.usedJSHeapSize < MEMORY_THRESHOLD_BYTES) return

      const reloaded = !isUnsafeToReload(pathnameRef.current)
      const heap = {
        usedJSHeapSize: memoryUsage.usedJSHeapSize,
        totalJSHeapSize: memoryUsage.totalJSHeapSize,
        jsHeapSizeLimit: memoryUsage.jsHeapSizeLimit
      }

      sendMemoryInventoryToSentry(apiQueryClient, {
        pathname: pathnameRef.current,
        heap
      })

      logEvent({
        eventName: 'memory_threshold_exceeded',
        metadata: {
          ...heap,
          pathname: pathnameRef.current,
          reloaded
        }
      })

      if (reloaded) {
        apiQueryClient.clear()
        window.location.reload()
      }
    } catch {
      // Ignore errors in case performance.memory is not accessible
    }
  }, [logEvent])

  useEffect(() => {
    const interval = setInterval(checkMemory, MEMORY_CHECK_INTERVAL)
    return () => clearInterval(interval)
  }, [checkMemory])
}
